import { CaretDown } from "@phosphor-icons/react";
import { t } from "@lingui/macro";
import { AspectRatio, ScrollArea } from "@reactive-resume/ui";
import { cn, templateGroups } from "@reactive-resume/utils";
import { normalizeToFileName } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../../shared/section-icon";

export const Templates = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ AcadéNice: true });

  const toggleGroup = (group: string) => {
    setOpenGroups((current) => ({ ...current, [group]: !(current[group] ?? false) }));
  };

  return (
    <section id="template" className="flex h-screen flex-col">
      <header className="flex items-center justify-between py-2">
        <div className="mx-auto flex items-center gap-x-4">
          <SectionIcon id="template" size={18} name={t`Template`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl/loose">{t`Template`}</h2>
        </div>
      </header>

      <ScrollArea className="h-full flex-1">
        <div className="space-y-8 px-6 py-2">
          {templateGroups.map((group) => {
            const isOpen = openGroups[group.name] ?? false;

            return (
              <section key={group.name} className="space-y-4">
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-sm px-1 py-1 text-left text-sm font-semibold uppercase tracking-wider text-foreground/70 transition-colors hover:text-foreground"
                  onClick={() => {
                    toggleGroup(group.name);
                  }}
                >
                  <span>{group.name}</span>
                  <span className="flex items-center gap-x-2 text-xs normal-case tracking-normal">
                    {group.templates.length}
                    <CaretDown
                      size={16}
                      className={cn("transition-transform", !isOpen && "-rotate-90")}
                    />
                  </span>
                </button>

                {isOpen && (
                  <div className="grid grid-cols-2 gap-8 @lg/right:grid-cols-3 @2xl/right:grid-cols-4">
                    {group.templates.map((template, index) => {
                      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                      const normalized = normalizeToFileName(template);

                      return (
                        <AspectRatio key={template} ratio={1 / 1.4142}>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { delay: index * 0.05 } }}
                            whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                            className={cn(
                              "relative cursor-pointer rounded-sm ring-acade-secondary-500 transition-all hover:ring-2",
                              currentTemplate === template && "ring-2",
                            )}
                            onClick={() => {
                              setValue("metadata.template", template);
                            }}
                          >
                            <img
                              src={`/templates/jpg/${normalized}.jpg`}
                              alt={`Aperçu du modèle ${template}`}
                              className="rounded-sm"
                            />

                            <div className="absolute inset-x-0 bottom-0 h-32 w-full bg-gradient-to-b from-transparent to-background/80">
                              <p className="absolute inset-x-0 bottom-2 text-center font-normal capitalize text-foreground">
                                {template}
                              </p>
                            </div>
                          </motion.div>
                        </AspectRatio>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </ScrollArea>
    </section>
  );
};
